import { mentionableSchema } from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AtSign } from 'lucide-react';
import {
  useDeferredValue,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
} from 'react';
import { useAuth } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { insertMention, mentionQueryAt, type MentionRef } from './mentions';

type MentionTextareaProps = Omit<ComponentProps<'textarea'>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (value: string) => void;
  /** Pessoas escolhidas na lista (o texto guarda "@Nome"). */
  mentions: MentionRef[];
  onMentionsChange: (mentions: MentionRef[]) => void;
  /** Rota da API com quem pode ser mencionado no assunto (`.../mentionable`). */
  mentionableUrl: string;
};

/**
 * Campo de comentário com menções (T-113d, RF-42; DEC-048): ao digitar "@", lista quem acompanha o assunto
 * (busca pelo que vem depois do "@"). Teclado: setas escolhem, Enter ou Tab mencionam, Esc fecha. No toque, basta
 * tocar no nome. A lista fica logo abaixo do campo (sem sobrepor nem ser cortada pelo painel).
 */
export function MentionTextarea({
  value,
  onValueChange,
  mentions,
  onMentionsChange,
  mentionableUrl,
  className,
  onKeyDown,
  ...props
}: MentionTextareaProps) {
  const { client } = useAuth();
  const ref = useRef<HTMLTextAreaElement>(null);
  const listId = useId();
  const [caret, setCaret] = useState(0);
  // Início do "@" que já foi resolvido (escolhido ou fechado com Esc): não reabre a lista para ele.
  const [closedAt, setClosedAt] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  const trigger = mentionQueryAt(value, caret);
  const open = trigger !== null && trigger.start !== closedAt;
  const query = useDeferredValue(trigger?.query.trim() ?? '');
  const people = useQuery({
    queryKey: ['mentionable', mentionableUrl, query],
    queryFn: ({ signal }) =>
      client.request(`${mentionableUrl}${query ? `?q=${encodeURIComponent(query)}` : ''}`, {
        schema: mentionableSchema,
        signal,
      }),
    enabled: open,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    meta: { handlesError: true },
  });
  const options = open ? (people.data?.items ?? []) : [];
  const activeIndex = Math.min(active, Math.max(0, options.length - 1));
  // Primeira busca depois do "@" (ainda sem resultado): mostra que está buscando.
  const searching = open && people.isFetching && !people.data;
  const showList =
    open && (options.length > 0 || people.isError || searching || (!people.isFetching && query !== ''));
  const triggerStart = trigger?.start ?? null;

  // Esc com a lista aberta fecha só a lista. O diálogo em volta (Radix) escuta o Esc em captura no documento,
  // antes do React: o ouvinte em captura na janela vem antes dele e impede que o diálogo feche (e perca o texto).
  useEffect(() => {
    if (!showList || triggerStart === null) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.target !== ref.current) return;
      event.preventDefault();
      event.stopPropagation();
      setClosedAt(triggerStart);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [showList, triggerStart]);

  const syncCaret = () => setCaret(ref.current?.selectionStart ?? value.length);

  function choose(person: MentionRef) {
    if (!trigger) return;
    const next = insertMention(value, trigger.start, caret, person.name);
    onValueChange(next.text);
    if (!mentions.some((m) => m.id === person.id)) onMentionsChange([...mentions, person]);
    setClosedAt(trigger.start);
    setCaret(next.caret);
    setActive(0);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(next.caret, next.caret);
    });
  }

  function keyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (open && options.length) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive((activeIndex + step + options.length) % options.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        choose(options[activeIndex]!);
        return;
      }
    }
    onKeyDown?.(event);
  }

  const optionId = (i: number) => `${listId}-opcao-${i}`;
  return (
    <div className="flex flex-col gap-1">
      <textarea
        {...props}
        ref={ref}
        value={value}
        role="combobox"
        aria-expanded={showList}
        aria-autocomplete="list"
        aria-controls={showList ? listId : undefined}
        aria-activedescendant={showList && options.length ? optionId(activeIndex) : undefined}
        onChange={(e) => {
          const nextCaret = e.target.selectionStart ?? e.target.value.length;
          onValueChange(e.target.value);
          setCaret(nextCaret);
          setActive(0);
          // Um "@" recém-digitado sempre abre a lista, mesmo na posição de um que já foi resolvido.
          if (mentionQueryAt(e.target.value, nextCaret)?.query === '') setClosedAt(null);
        }}
        onSelect={syncCaret}
        onKeyDown={keyDown}
        onBlur={(e) => {
          // A lista fecha ao sair do campo (o toque num nome não tira o foco: ver onPointerDown abaixo).
          setClosedAt(trigger?.start ?? null);
          props.onBlur?.(e);
        }}
        onFocus={(e) => {
          setClosedAt(null);
          syncCaret();
          props.onFocus?.(e);
        }}
        className={className}
      />
      {showList ? (
        <div className="rounded-md border border-border-strong bg-surface-raised p-1 shadow-md">
          {options.length ? (
            <ul id={listId} role="listbox" aria-label="Pessoas para mencionar" className="flex flex-col">
              {options.map((person, i) => (
                <li
                  key={person.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === activeIndex}
                  // Mantém o foco no campo ao tocar/clicar (senão a lista fecharia antes da escolha).
                  onPointerDown={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(person)}
                  className={cn(
                    'flex min-h-11 cursor-pointer items-center gap-2 rounded-sm px-2 text-sm',
                    i === activeIndex ? 'bg-brand-soft text-brand-strong' : 'hover:bg-surface',
                  )}
                >
                  <AtSign className="size-3.5 shrink-0 opacity-70" aria-hidden />
                  <span className="truncate">{person.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p id={listId} className="px-2 py-1.5 text-sm text-muted-foreground">
              {people.isError
                ? 'Não foi possível buscar as pessoas. Continue digitando para tentar de novo.'
                : searching
                  ? 'Buscando pessoas…'
                  : 'Ninguém com esse nome acompanha este item.'}
            </p>
          )}
        </div>
      ) : null}
      <p className="sr-only" aria-live="polite">
        {showList && options.length
          ? `${options.length === 1 ? '1 pessoa encontrada' : `${options.length} pessoas encontradas`}. Use as setas e Enter para mencionar.`
          : ''}
      </p>
    </div>
  );
}
